import React, {useCallback, useMemo} from 'react';
import {classes, style} from './doppe-layout1-profile-view-template.st.css';
import {BymoTemplateMetadataInferBymoPageProps, useBymoPageContextProps} from '../../../doppe-sdk';
import {ExpandableText, MultilineExpandableText, RatioMaintainer, useObservable} from '@wix/devzai-utils-react';
import {
    arrayFirst,
    arrayIsNotNullableOrEmpty,
    evaluateFunction,
    iterableAny,
    ObservableSet,
    stringIsNotNullableOrWhiteSpace,
    Values
} from '@wix/devzai-utils-common';
import {FlexLayout} from '@wix/devzai-common-client';
import {
    doppeHideableValueCreateHidden,
    doppeHideableValueGetVisibleValue,
    doppeHideableValueIsVisible
} from '../../../client-server-common/types/doppe-hideable-value';
import {DoppeHideableValueRenderer} from '../../doppe-hideable-value-renderer/doppe-hideable-value-renderer';
import {DoppePageProfileNameView} from '../../doppe-page-profile-name-view/doppe-page-profile-name-view';
import {bymoPageSocialLinkIsHidden, DoppeProfileNameAutoSource} from '../../../client-server-common';
import {DoppePageSocialLinks} from '../../doppe-page-social-links/doppe-page-social-links';
import {useDoppeViewerLangDictionary} from '../../doppe-viewer-lang-dictionary/doppe-viewer-lang-dictionary';
import {DoppeViewerActionContext} from '../../doppe-viewer-action-context/doppe-viewer-action-context';
import {DoppeViewerAction} from '../../doppe-viewer-action/doppe-viewer-action';
import {DoppeTemplateBasedActionView} from '../../doppe-template-based-action-view/doppe-template-based-action-view';
import {
    DoppeActionViewInnerContentCloseButton
} from '../../doppe-action-view-inner-content-close-button/doppe-action-view-inner-content-close-button';
import {
    doppeDtoActionGetDefaultProps,
    DoppeDtoActionViewType
} from '../../../client-server-common/types/doppe-dto-action';
import {
    BymoTemplateSpeedDialActionsListAppearance,
    BymoTemplateSpeedDialAutoComplete,
    BymoTemplateSpeedDialSearchAppearance,
    BymoTemplateSpeedDialTemplateMetadata
} from '../../bymo-templates/bymo-template-speed-dial/bymo-template-speed-dial.metadata';
import {
    doppeActionTypeSpeedDial
} from '../../../doppe-action-types/doppe-action-type-speed-dial/doppe-action-type-speed-dial';
import {DoppeViewerActionsList} from '../../doppe-viewer-actions-list/doppe-viewer-actions-list';
import {DoppeLayoutRoot} from '../dopp-layouts-components/dopp-layout-root/doppe-layout-root';
import {DoppLayoutContent} from '../dopp-layouts-components/dopp-layout-content/dopp-layout-content';
import {DoppLayoutBackground} from '../dopp-layouts-components/dopp-layout-background/dopp-layout-background';
import {
    DoppLayoutSeparator,
    DoppLayoutSeparatorSkin
} from '../dopp-layouts-components/dopp-layout-separator/dopp-layout-separator';
import {
    DoppLayoutBackgroundMedia
} from '../dopp-layouts-components/dopp-layout-background-media/dopp-layout-background-media';
import {
    DoppLayoutContentHeader
} from '../dopp-layouts-components/dopp-layout-content-header/dopp-layout-content-header';
import {DoppePageProfilePicture} from '../../doppe-page-profile-picture/doppe-page-profile-picture';
import {DoppePageHeroPhoto} from '../../doppe-page-hero-photo/doppe-page-hero-photo';
import {doppeViewerShouldRemoveHoppBanner} from '../../doppe-viewer-utils/doppe-viewer-utils';
import {DoppLayoutContentTitle} from '../dopp-layouts-components/dopp-layout-content-title/dopp-layout-content-title';

export const DoppeLayout1ProfileViewSkin = {
    Standard: 'standard',
    WithGradient: 'withGradient',
} as const;

export type DoppeLayout1ProfileViewSkin = Values<typeof DoppeLayout1ProfileViewSkin>;

export interface DoppeLayout1ProfileViewProps {
    skin?: DoppeLayout1ProfileViewSkin;
}

export const DoppeLayout1ProfileViewTemplate = React.memo(function DoppeLayout1ProfileView(props: BymoTemplateMetadataInferBymoPageProps<typeof BymoTemplateSpeedDialTemplateMetadata> & DoppeLayout1ProfileViewProps) {
    const {
        skin = DoppeLayout1ProfileViewSkin.WithGradient,
        profilePictureLinkInBio,
        bioText,
        pageTitle,
        actionsHeading,
        bioTextMaxLines,
        socialLinks,
        searchAppearance,
        actionButtonTemplate,
        actionsListAppearance,
        actions,
        callToActionText,
        heroPhotos,
        searchPlaceholder
    } = props;


    const viewerLang = useDoppeViewerLangDictionary();
    const contextProps = useBymoPageContextProps();

    const bioTextVisibleValue = doppeHideableValueGetVisibleValue(bioText, '');

    const visibleSocialLinks = socialLinks.filter(socialLink => !bymoPageSocialLinkIsHidden(socialLink));
    const searchSectionIsVisible = searchAppearance !== BymoTemplateSpeedDialSearchAppearance.Hidden;

    const showProfileSeparator =
        (stringIsNotNullableOrWhiteSpace(bioTextVisibleValue) || arrayIsNotNullableOrEmpty(visibleSocialLinks)) &&
        searchSectionIsVisible;

    const actionsHeadingVisibleValue = doppeHideableValueGetVisibleValue(actionsHeading, '');


    const hasProfileImage = useMemo(() => {
        return doppeHideableValueIsVisible(profilePictureLinkInBio);
    }, [profilePictureLinkInBio]);


    const actionViewRenderFunction = useCallback<DoppeViewerAction.ActionViewRenderFunction>(renderProps => {
        return (
            <DoppeTemplateBasedActionView
                {...renderProps.actionViewContextProps}
                actionViewTemplate={actionButtonTemplate}
                renderInlineContentCloseButton={renderProps => {
                    return (
                        <DoppeActionViewInnerContentCloseButton
                            onCloseButtonClick={renderProps.closingFunction}
                        />
                    )
                }}
            />
        )
    }, [actionButtonTemplate])

    const fullScreenToggledActionsSet = useObservable(useMemo(() => new ObservableSet(), []));

    const fullScreenModeTogglingController = useMemo<DoppeViewerActionContext.FullScreenModeTogglingController>(() => {
        return {
            toggleFullScreenMode(actionId, isFullScreen) {
                fullScreenToggledActionsSet.toggleValue(actionId, isFullScreen);
            }
        }
    }, []);

    const hasActions = useMemo(()=>{
        return iterableAny(actions, action => action.enabled);
    }, [actions]);

    const removeHoppBanner = doppeViewerShouldRemoveHoppBanner(props, contextProps);

    return (
        <DoppeLayoutRoot
            className={style(classes.root, {
                skin: skin,
                withProfileImage: hasProfileImage,
                hasHeroPhotos: true,
                searchSectionIsVisible: searchSectionIsVisible,
                fullScreen: !fullScreenToggledActionsSet.isEmpty(),
                hideFooterBanner: removeHoppBanner
            })}
            hideFooterBanner={!fullScreenToggledActionsSet.isEmpty() || removeHoppBanner}
            fullScreen={!fullScreenToggledActionsSet.isEmpty()}
        >
            <DoppLayoutBackground
                className={style(classes.coverBackground)}
            >
                <RatioMaintainer
                    widthHeightRatio={3.3 / 3}
                >
                    <DoppePageHeroPhoto
                        className={style(classes.heroPhoto)}
                        mediaResource={heroPhotos === null ? null : (arrayFirst(heroPhotos) ?? null)}
                        layoutSpec={{
                            widthHeightRatio: 3.3 / 3
                        }}
                        widthSpec={{contentRelativeWidth: 1}}
                    />
                    <div className={style(classes.coverImageOverlay, {})}/>
                </RatioMaintainer>
                <div
                    className={style(classes.pageOverlayContainer, {})}
                >
                    <RatioMaintainer
                        widthHeightRatio={3.3 / 3}
                    />
                    <RatioMaintainer
                        className={style(classes.pageWithCoverBackgroundViewOverlay, {})}
                        widthHeightRatio={3.3 / 3}
                    />
                </div>
                <DoppLayoutBackgroundMedia
                    className={style(classes.pageWithCoverBackgroundView, {})}
                />
            </DoppLayoutBackground>
            <DoppLayoutContent
                className={style(classes.content)}
            >
                <div
                    className={style(classes.profileView, FlexLayout.column.alignToCenter)}
                >
                    {
                        hasProfileImage ?
                            <DoppeHideableValueRenderer
                                hideableValue={profilePictureLinkInBio}
                                renderFunction={profilePicture => (
                                    <DoppePageProfilePicture
                                        className={style(classes.profileImage, {})}
                                        profilePicture={profilePicture}
                                        size={104}
                                    />
                                )}
                            />
                            :
                            null
                    }
                    <DoppePageProfileNameView
                        className={style(classes.profileName, {}, FlexLayout.verticallyJustifyToEnd)}
                        defaultProfileNameAutoSource={DoppeProfileNameAutoSource.AccountName}
                    />

                    {stringIsNotNullableOrWhiteSpace(bioTextVisibleValue) ? evaluateFunction(() => {

                        const maxVisibleLines =
                            doppeHideableValueGetVisibleValue(bioTextMaxLines, Infinity);

                        if (maxVisibleLines === Infinity) {
                            return (
                                <div
                                    className={style(classes.bioText, {})}
                                >
                                    {bioTextVisibleValue}
                                </div>
                            )
                        } else if (maxVisibleLines > 1) {
                            return (
                                <MultilineExpandableText
                                    className={style(classes.bioTextMultiline, {})}
                                    text={bioTextVisibleValue}
                                    expandButtonText={viewerLang.seeMore}
                                    lineHeight={'18px'}
                                    maxLines={maxVisibleLines}
                                    collapseOnTextChange={true}
                                />
                            )
                        } else {
                            return (
                                <ExpandableText
                                    className={style(classes.bioText, {})}
                                    seeMoreButtonContent={(
                                        <div
                                            className={style(classes.seeMoreButton, {})}>{viewerLang.seeMore}</div>
                                    )}
                                >
                                    {bioTextVisibleValue}
                                </ExpandableText>
                            )
                        }
                    }) : null}
                    {arrayIsNotNullableOrEmpty(visibleSocialLinks) ? (
                        <DoppePageSocialLinks
                            className={style(classes.socialLinks)}
                            socialLinks={visibleSocialLinks}
                        />
                    ) : null}
                    {showProfileSeparator ? (
                        <DoppLayoutSeparator
                            skin={DoppLayoutSeparatorSkin.Profile}
                        />
                    ) : null}
                </div>
                <div
                    className={style(classes.pageContent, {})}
                >
                    <DoppLayoutContentTitle
                        text={pageTitle}
                    />
                    {searchSectionIsVisible ? (
                        <>
                            <DoppeHideableValueRenderer
                                hideableValue={callToActionText}
                                renderFunction={callToActionText => {

                                    const normalizedCallToActionText = callToActionText.trim();

                                    return normalizedCallToActionText !== '' ? (
                                        <DoppLayoutContentHeader
                                            className={style(classes.supportText)}
                                            text={normalizedCallToActionText}
                                        />
                                    ) : null
                                }}
                            />
                            <div
                                className={style(classes.codeWidget, {})}
                            >
                                <DoppeViewerAction
                                    action={{
                                        ...doppeDtoActionGetDefaultProps(),
                                        viewType: DoppeDtoActionViewType.InPage,
                                        settings: {
                                            autoCompleteBehaviour: BymoTemplateSpeedDialAutoComplete.OnInputFocus,
                                            showFullScreenAutoCompleteOnMobile: true,
                                            searchPlaceholder: searchPlaceholder
                                        },
                                        actionTypeId: doppeActionTypeSpeedDial.id,
                                        callToActionDescription: doppeHideableValueCreateHidden(''),
                                        enabled: true,
                                        id: 'SPEED_DIAL',
                                        name: `Enter a Code`
                                    }}
                                    renderActionView={actionViewRenderFunction}
                                    fullScreenModeTogglingController={fullScreenModeTogglingController}
                                />
                            </div>
                        </>
                    ) : null}
                    {actionsListAppearance !== BymoTemplateSpeedDialActionsListAppearance.Hidden  ? (
                        <>
                            {
                                searchSectionIsVisible &&  (hasActions || actionsHeadingVisibleValue ) ?
                                    <DoppLayoutSeparator
                                        skin={DoppLayoutSeparatorSkin.Actions}
                                    />
                                    :
                                    null
                            }
                            <DoppLayoutContentHeader
                                text={actionsHeading}
                            />
                            {
                                hasActions ?
                                    <DoppeViewerActionsList
                                        className={style(classes.actionsList, {})}
                                        supportsRenderingFramelessWidgets={true}
                                        renderActionView={actionViewRenderFunction}
                                        fullScreenModeTogglingController={fullScreenModeTogglingController}
                                    />
                                    :
                                    null
                            }
                        </>
                    ) : null}
                </div>
            </DoppLayoutContent>
        </DoppeLayoutRoot>
    )
});

